import { useState, useCallback, FormEvent, useEffect } from 'react';
import * as moment from 'moment';
import { useTranslation } from 'react-i18next';
import { CheckField } from 'ui/shared/controls';
import { EcsBuilding, BuildingId, EcsProperties } from 'shared/models';
import { Loading } from 'ui/shared/loading';
import { DebugLink } from 'ui/shared/DebugLink';
import { useSession } from 'hooks';
import { Permission } from 'shared/permissions';

require('./BuildingEcsForm.css');

export type Props = {
    buildingId: BuildingId | null;
    data: EcsBuilding | null;
    isLoading: boolean;
    isSaving: boolean;
    onClickSave(data: Omit<EcsBuilding, 'lastUpdated'>): void;
};

export const BuildingEcsForm = ({ buildingId, data, isLoading, isSaving, onClickSave }: Props) => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();

    const enabledUntil = moment.utc(data ? data.enabledUntil : 0);

    const [ hasOneWayVideo, setHasOneWayVideo ] = useState(!!data);
    const [ allowUnsolicitedCalls, setAllowUnsolicitedCalls ] = useState(data ? data.properties.allowUnsolicitedCalls : null);
    const [ disableSensitiveData, setDisableSensitiveData ] = useState(data?.properties?.disableSensitiveData || false);
    const [ allowTurnServer, setAllowTurnServer ] = useState(data ? !data.properties.disableTurn : null);
    const [ isFactory, setIsFactory ] = useState(data ? data.properties.factory : null);
    const [ isGsa, setIsGsa ] = useState(data ? data.properties.gsa : null);
    const [ year, setYear ]   = useState<string | null>(data ? enabledUntil.year().toString() : null);
    const [ month, setMonth ] = useState<string | null>(data ? enabledUntil.month().toString() : null);
    const [ day, setDay ]     = useState<string | null>(data ? enabledUntil.day().toString() : null);
    
    const dataLoaded = !!data;
    const properties: EcsProperties = data ? data.properties : {};

    // Update the current internal state if the input data changes
    useEffect(() => {
        setHasOneWayVideo(dataLoaded);
        setAllowUnsolicitedCalls(!!properties.allowUnsolicitedCalls);
        setDisableSensitiveData(!!properties.disableSensitiveData);
        setAllowTurnServer(!properties.disableTurn);
        setIsFactory(!!properties.factory);
        setIsGsa(!!properties.gsa);
        setYear(data ? enabledUntil.year().toString() : null);
        setMonth(data ? enabledUntil.month().toString() : null);
        setDay(data ? enabledUntil.day().toString() : null);
    }, [
        dataLoaded, properties.allowUnsolicitedCalls, properties.disableSensitiveData, properties.disableTurn, properties.factory, properties.gsa, enabledUntil.valueOf(),
        setHasOneWayVideo, setAllowUnsolicitedCalls, setDisableSensitiveData, setAllowTurnServer, setIsFactory, setIsGsa, setYear, setMonth, setDay,
    ]);

    const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!buildingId || data === null || allowUnsolicitedCalls === null || disableSensitiveData === null) return;

        //const enabledUntil = moment.utc(Date.UTC(parseInt(year!, 10), parseInt(month!, 10), parseInt(day!, 10)));
        onClickSave({
            buildingId,
            properties: {
                ...data.properties,
                allowUnsolicitedCalls,
                disableSensitiveData,
                disableTurn: !allowTurnServer,
                factory: !!isFactory,
                gsa: !!isGsa,
            },
            enabledUntil: Date.now() + 365 * 24 * 60 * 60 * 1000, //enabledUntil.isValid() ? enabledUntil.valueOf() : 0,
        });
        return false;
    }, [ onClickSave, buildingId, data, allowUnsolicitedCalls, disableSensitiveData, allowTurnServer, isFactory, isGsa /*, day, month, year*/ ]);

    if (isLoading) {
        return (
            <Loading/>
        );
    }
    return (
        <form
            id="building-ecs-form"
            onSubmit={ handleSubmit }
            className={ `ui form${isSaving ? ' loading' : ''}` }
        >
            { /* TODO...
            <div className="ui grid">
                <div className="five wide column">
                    <TextField
                        id="ecs-expiry"
                        required={ true }
                        labelKey="BUILDING.FIELD.ENABLED_UNTIL"
                        maxLength={ 4 }
                        value={ ... }
                        onChange={ e => setWidth(e.currentTarget.value) }
                        error={ null }
                        meta={ {
                            error: '',
                        } }
                        disabled={ !hasOneWayVideo }
                    />
                </div>
                <div className="eleven wide column">
                    <button
                        className="ui gray button"
                        type="button"
                        onClick={ () => setYear(year! + 1) }
                    >
                        { t('BUILDING.ECS.ADD_YEAR') }
                    </button>
                </div>
            </div>
            */ }

            <div className="ui grid">
                <div className="eight wide column">
                    <CheckField
                        id="ecs-allow-unsolicited-calls"
                        labelKey="BUILDING.FIELD.ALLOW_UNSOLICITED_CALLS"
                        disabled={ !hasOneWayVideo }
                        input={ {
                            checked: !!allowUnsolicitedCalls,
                            onChange: (e: FormEvent<HTMLInputElement>) => setAllowUnsolicitedCalls(e.currentTarget.checked),
                        } }
                    />
                    <p className="field-description">
                        { t('BUILDING.FIELD.ALLOW_UNSOLICITED_CALLS_DESC')}
                    </p>
                </div>
            </div>

            { hasPermission(buildingId, Permission.EditBuildingFeatures) &&
                <div className="ui grid">
                    <div className="eight wide column">
                        <CheckField
                            id="ecs-disable-sensitive-data"
                            labelKey="BUILDING.FIELD.DISABLE_SENSITIVE_DATA"
                            disabled={ !hasOneWayVideo }
                            input={ {
                                checked: !!disableSensitiveData,
                                onChange: (e: FormEvent<HTMLInputElement>) => setDisableSensitiveData(e.currentTarget.checked),
                            } }
                        />
                        <p className="field-description">
                            { t('BUILDING.FIELD.DISABLE_SENSITIVE_DATA_DESC')}
                        </p>
                    </div>
                </div>
            }

            { hasPermission(buildingId, Permission.EditBuildingFeatures) && hasPermission(null, Permission.AllBuildings) &&
                <div className="ui grid">
                    <div className="eight wide column">
                        <CheckField
                            id="ecs-allow-turn-server"
                            labelKey="BUILDING.FIELD.ALLOW_TURN_SERVER"
                            disabled={ !hasOneWayVideo }
                            input={ {
                                checked: !!allowTurnServer,
                                onChange: (e: FormEvent<HTMLInputElement>) => setAllowTurnServer(e.currentTarget.checked),
                            } }
                        />
                        <p className="field-description">
                            { t('BUILDING.FIELD.ALLOW_TURN_SERVER_DESC')}
                        </p>
                    </div>
                </div>
            }

            { hasPermission(buildingId, Permission.EditBuildingFeatures) && hasPermission(null, Permission.AllBuildings) &&
                <div className="ui grid">
                    <div className="eight wide column">
                        <CheckField
                            id="ecs-factory"
                            labelKey="BUILDING.FIELD.FACTORY"
                            disabled={ !hasOneWayVideo }
                            input={ {
                                checked: !!isFactory,
                                onChange: (e: FormEvent<HTMLInputElement>) => setIsFactory(e.currentTarget.checked),
                            } }
                        />
                        <p className="field-description">
                            { t('BUILDING.FIELD.FACTORY_DESC')}
                        </p>
                    </div>
                </div>
            }

            { hasPermission(null, Permission.Admin) &&
                <div className="ui grid">
                    <div className="eight wide column">
                        <CheckField
                            id="ecs-gsa"
                            labelKey="BUILDING.FIELD.GSA"
                            disabled={ !hasOneWayVideo }
                            input={ {
                                checked: !!isGsa,
                                onChange: (e: FormEvent<HTMLInputElement>) => setIsGsa(e.currentTarget.checked),
                            } }
                        />
                        <p className="field-description">
                            { t('BUILDING.FIELD.GSA_DESC')}
                        </p>
                    </div>
                </div>
            }

            { hasOneWayVideo &&
                <button
                    className="ui primary submit button"
                    type="submit"
                    disabled={ isSaving }
                >
                    <div className="content">
                        { t('COMMAND.SAVE') }
                    </div>
                </button>
            }
            { !hasOneWayVideo &&
                <button
                    className="ui primary submit button"
                    type="button"
                    onClick={ () => setHasOneWayVideo(true) }
                >
                    <div className="content">
                        { t('BUILDING.FIELD.ENABLE_ECS') }
                    </div>
                </button>
            }
            
            <DebugLink sql={ `SELECT *\nFROM ecs_buildings\nWHERE building_id = '${buildingId}';\n` }/>
        </form>
    );
};
